import { openDB, type IDBPDatabase } from 'idb';
import type { Song, Playlist, StorageAdapter } from '@/types';

const DB_NAME = 'zemer-music-db';
const DB_VERSION = 1;

interface ZemerDB {
  favorites: {
    key: string;
    value: Song;
  };
  playlists: {
    key: string;
    value: Playlist;
  };
  history: {
    key: number;
    value: {
      song: Song;
      timestamp: number;
    };
  };
  settings: {
    key: string;
    value: unknown;
  };
}

class IndexedDBStorage implements StorageAdapter {
  private dbPromise: Promise<IDBPDatabase<ZemerDB>>;

  constructor() {
    this.dbPromise = this.initDB();
  }

  private async initDB(): Promise<IDBPDatabase<ZemerDB>> {
    return openDB<ZemerDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Favorites store
        if (!db.objectStoreNames.contains('favorites')) {
          db.createObjectStore('favorites', { keyPath: 'videoId' });
        }

        // Playlists store
        if (!db.objectStoreNames.contains('playlists')) {
          db.createObjectStore('playlists', { keyPath: 'id' });
        }

        // History store
        if (!db.objectStoreNames.contains('history')) {
          const historyStore = db.createObjectStore('history', {
            keyPath: 'timestamp',
            autoIncrement: false,
          });
          historyStore.createIndex('by-timestamp', 'timestamp');
        }

        // Settings store
        if (!db.objectStoreNames.contains('settings')) {
          db.createObjectStore('settings');
        }
      },
    });
  }

  async get<T>(key: string): Promise<T | null> {
    const db = await this.dbPromise;
    const value = await db.get('settings', key);
    return value as T ?? null;
  }

  async set<T>(key: string, value: T): Promise<void> {
    const db = await this.dbPromise;
    await db.put('settings', value, key);
  }

  async delete(key: string): Promise<void> {
    const db = await this.dbPromise;
    await db.delete('settings', key);
  }

  async clear(): Promise<void> {
    const db = await this.dbPromise;
    await db.clear('settings');
  }

  // Favorites methods
  async addFavorite(song: Song): Promise<void> {
    const db = await this.dbPromise;
    await db.put('favorites', song);
  }

  async removeFavorite(videoId: string): Promise<void> {
    const db = await this.dbPromise;
    await db.delete('favorites', videoId);
  }

  async getFavorites(): Promise<Song[]> {
    const db = await this.dbPromise;
    return db.getAll('favorites');
  }

  async isFavorite(videoId: string): Promise<boolean> {
    const db = await this.dbPromise;
    const song = await db.get('favorites', videoId);
    return !!song;
  }

  // Playlist methods
  async createPlaylist(playlist: Playlist): Promise<void> {
    const db = await this.dbPromise;
    await db.add('playlists', playlist);
  }

  async updatePlaylist(playlist: Playlist): Promise<void> {
    const db = await this.dbPromise;
    await db.put('playlists', playlist);
  }

  async deletePlaylist(id: string): Promise<void> {
    const db = await this.dbPromise;
    await db.delete('playlists', id);
  }

  async getPlaylist(id: string): Promise<Playlist | null> {
    const db = await this.dbPromise;
    const playlist = await db.get('playlists', id);
    return playlist ?? null;
  }

  async getAllPlaylists(): Promise<Playlist[]> {
    const db = await this.dbPromise;
    return db.getAll('playlists');
  }

  // History methods
  async addToHistory(song: Song): Promise<void> {
    const db = await this.dbPromise;
    const timestamp = Date.now();
    await db.put('history', { song, timestamp });

    // Keep only last 100 items
    const allHistory = await db.getAllFromIndex('history', 'by-timestamp');
    if (allHistory.length > 100) {
      const toDelete = allHistory.slice(0, allHistory.length - 100);
      const tx = db.transaction('history', 'readwrite');
      for (const item of toDelete) {
        await tx.store.delete(item.timestamp);
      }
      await tx.done;
    }
  }

  async getHistory(limit: number = 50): Promise<Array<{ song: Song; timestamp: number }>> {
    const db = await this.dbPromise;
    const all = await db.getAllFromIndex('history', 'by-timestamp');
    return all.slice(-limit).reverse();
  }

  async clearHistory(): Promise<void> {
    const db = await this.dbPromise;
    await db.clear('history');
  }
}

// Export singleton
export const storage = new IndexedDBStorage();

// Export class for testing
export { IndexedDBStorage };
